/**
 * What every action form is built out of: the platform vocabulary, the two
 * pieces of chrome each form repeats, and the one way a failed submit is
 * turned into a sentence.
 *
 * Extracted because three widgets use all of it and the widget file was over
 * the 400-line ceiling — not to make anything reusable that is not already
 * used more than once.
 */
import React from "react";
import {useSelector} from "react-redux";
import {FaTiktok, FaInstagram, FaFacebook, FaXTwitter, FaYoutube, FaTwitch, FaGlobe} from "react-icons/fa6";
import {IStore} from "@/redux/defaultStore";
import "./earnActionForms.scss";


/*
 * The PLATFORM table — icon, display name, and the URL shape a member is
 * being asked for. This is knowledge about the platforms themselves, so it
 * lives in code; WHICH of them an objective accepts is policy and arrives
 * from the assignment. A platform with no entry still works: it renders its
 * own name and a generic example.
 */
const PLATFORM_META: Record<string, { label: string; Icon: React.ElementType; example: string }> = {
    tiktok: {label: "TikTok", Icon: FaTiktok, example: "https://www.tiktok.com/@yourhandle/video/…"},
    instagram: {label: "Instagram", Icon: FaInstagram, example: "https://www.instagram.com/p/…"},
    facebook: {label: "Facebook", Icon: FaFacebook, example: "https://www.facebook.com/…/posts/…"},
    twitter: {label: "X", Icon: FaXTwitter, example: "https://x.com/…/status/…"},
    x: {label: "X", Icon: FaXTwitter, example: "https://x.com/…/status/…"},
    youtube: {label: "YouTube", Icon: FaYoutube, example: "https://www.youtube.com/watch?v=…"},
    twitch: {label: "Twitch", Icon: FaTwitch, example: "https://www.twitch.tv/videos/…"},
};

export function platformMeta(platform: string): { label: string; Icon: React.ElementType; example: string } {
    return PLATFORM_META[platform.toLowerCase()] ?? {
        label: platform.charAt(0).toUpperCase() + platform.slice(1),
        Icon: FaGlobe,
        example: "https://…",
    };
}

/** Renders with the hash whether or not the campaign authored one. */
export function asHashtag(tag: string): string {
    return tag.startsWith("#") ? tag : `#${tag}`;
}

/**
 * The platform row. ONE accepted platform locks — the member is not being
 * asked to choose, they are being told where it counts, which is the shape
 * a per-round platform rotation takes once the round is configuration.
 * Several are a real choice.
 */
export function PlatformPills({platforms, selected, onSelect}: {
    platforms: string[];
    selected: string;
    onSelect: (platform: string) => void;
}): React.ReactElement | null {
    if (!platforms.length) return null;
    const locked = platforms.length === 1;
    return (
        /*
         * `group`, not `radiogroup`: these are toggle buttons with
         * aria-pressed, and announcing a radiogroup promises arrow-key
         * navigation between radios that does not exist here.
         */
        <div className="earn-action__platforms" role="group" aria-label="Platform">
            {platforms.map(platform => {
                const {label, Icon} = platformMeta(platform);
                const isOn = platform.toLowerCase() === selected.toLowerCase();
                return (
                    <button
                        key={platform}
                        type="button"
                        className={`earn-action__platform ${isOn ? "earn-action__platform--on" : ""}`}
                        onClick={locked ? undefined : () => onSelect(platform)}
                        disabled={locked}
                        aria-pressed={locked ? undefined : isOn}
                        aria-label={locked ? `${label} — the platform for this action` : undefined}
                    >
                        <Icon size={16} />
                        <span>{label}</span>
                    </button>
                );
            })}
        </div>
    );
}

/** "2 submissions left this period" — only when the assignment caps them. */
export function RemainingLine({remaining, noun}: { remaining?: number; noun: string }): React.ReactElement | null {
    if (remaining === undefined) return null;
    return (
        <p className="earn-action__remaining">
            {remaining} {noun}{remaining === 1 ? "" : "s"} left this period
        </p>
    );
}

/**
 * The generated client throws ResponseError (an Error carrying `.response`)
 * for non-2xx; the API's error envelope carries the user-facing message (the
 * routes write them to be shown: "Likes on x.com don't count this period.").
 * Matched by the `.response` field, not `instanceof ResponseError` — the class
 * can exist twice in a module graph (measured under vitest) and instanceof
 * then lies. Unparseable failures fall back to a generic line.
 */
export async function actionErrorMessage(err: unknown): Promise<string> {
    let response: Response | undefined;
    if (err instanceof Response) {
        response = err;
    } else if ((err as { response?: unknown })?.response instanceof Response) {
        response = (err as { response: Response }).response;
    }
    if (response) {
        try {
            const body = await response.clone().json() as { message?: string };
            if (body?.message) return body.message;
        } catch { /* fall through */ }
    }
    return "That didn't go through. Please try again.";
}

export interface ActionOutcome {
    tone: "ok" | "info" | "error";
    text: string;
}

/**
 * The action form's own status line, for the window before the policy has
 * arrived and for when it never does.
 *
 * Both used to render as silence: `policy` is `undefined` while loading AND
 * on a 404, so a misconfigured objective drew a normal-looking form with no
 * platforms and no hashtag, and the member only discovered it by submitting.
 *
 * SIGNED OUT IS NOT BROKEN. The policy query is disabled without a token, and
 * a disabled react-query v5 query is neither loading nor errored — so a signed-
 * out member got that same empty form and no sentence at all, while anyone who
 * did reach an error branch was told the action "isn't available", which reads
 * as "we took it down" rather than "you are not signed in". The three states
 * are now three answers.
 */
export function PolicyStatus({isLoading, isError}: { isLoading: boolean; isError: boolean }): React.ReactElement | null {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    if (!fullToken) {
        return (
            <p className="earn-action__remaining" role="status">
                Sign in to claim this.
            </p>
        );
    }
    if (isError) {
        return (
            <p className="earn-action__outcome earn-action__outcome--error" role="status">
                This action isn&apos;t available right now.
            </p>
        );
    }
    if (isLoading) {
        return <p className="earn-action__remaining" role="status">Loading…</p>;
    }
    return null;
}
